import { getRandomInt } from "../Shared/Helpers.tsx";
import { ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { agreeAfterNumber as agreeEnglish } from "./Grammar.ts";
import { agreeAfterNumber as agreeSpanish } from "./Grammar.es.ts";
import { agreeAfterNumber as agreeFrench } from "./Grammar.fr.ts";
import AdditionStories from "../Files/WordProblems/Addition.json";
import SubtractionStories from "../Files/WordProblems/Subtraction.json";
import MultiplicationStories from "../Files/WordProblems/Multiplication.json";
import DivisionStories from "../Files/WordProblems/Division/Simple.json";
import RemainderStories from "../Files/WordProblems/Division/Remainders.json";
import AdditionStoriesEs from "../Files/WordProblems/es/Addition.json";
import SubtractionStoriesEs from "../Files/WordProblems/es/Subtraction.json";
import MultiplicationStoriesEs from "../Files/WordProblems/es/Multiplication.json";
import DivisionStoriesEs from "../Files/WordProblems/es/Division/Simple.json";
import RemainderStoriesEs from "../Files/WordProblems/es/Division/Remainders.json";
import AdditionStoriesFr from "../Files/WordProblems/fr/Addition.json";
import SubtractionStoriesFr from "../Files/WordProblems/fr/Subtraction.json";
import MultiplicationStoriesFr from "../Files/WordProblems/fr/Multiplication.json";
import DivisionStoriesFr from "../Files/WordProblems/fr/Division/Simple.json";
import RemainderStoriesFr from "../Files/WordProblems/fr/Division/Remainders.json";
import MoneyAdditionStories from "../Files/WordProblems/Money/Addition.json";
import MoneySubtractionStories from "../Files/WordProblems/Money/Subtraction.json";
import MoneyMultiplicationStories from "../Files/WordProblems/Money/Multiplication.json";
import { canTakeVariedStory, storyTypesFor, TYPE_STORIES } from "./StoryTypes.ts";
import NamesJson from "../Files/Names.json";
import NamesEsJson from "../Files/NamesEs.json";
import NamesFrJson from "../Files/NamesFr.json";

/** ADR 0016's story language, widened to 'fr' by ADR 0051 part 3. Same value as `PrintedLanguage`. */
type Language = PrintedLanguage;

/**
 * Turns arithmetic into word problems.
 *
 * This used to happen in the renderer, which drew a story and a name with
 * Math.random() every time it was called. Pages renders a worksheet and its
 * answer key from the same problem data but in two separate calls, so the two
 * draws differed and the key described a different worksheet than the one
 * printed - a different story, a different name, about different objects.
 *
 * Choosing the story here makes it part of the problem, so both pages render
 * the same one. It also means the answer can account for a remainder, which
 * the renderer had no way to do.
 */

interface StoryTemplate {
    id?: number;
    story: string;
    question: string;
    problem: string;
}

const NAMES: Record<Language, string[]> = {
    en: NamesJson.names,
    es: NamesEsJson.names,
    fr: NamesFrJson.names,
};

const STORIES: Record<Language, {
    add: StoryTemplate[];
    sub: StoryTemplate[];
    mul: StoryTemplate[];
    div: StoryTemplate[];
    rem: StoryTemplate[];
}> = {
    en: {
        add: AdditionStories,
        sub: SubtractionStories,
        mul: MultiplicationStories,
        div: DivisionStories,
        rem: RemainderStories,
    },
    es: {
        add: AdditionStoriesEs,
        sub: SubtractionStoriesEs,
        mul: MultiplicationStoriesEs,
        div: DivisionStoriesEs,
        rem: RemainderStoriesEs,
    },
    fr: {
        add: AdditionStoriesFr,
        sub: SubtractionStoriesFr,
        mul: MultiplicationStoriesFr,
        div: DivisionStoriesFr,
        rem: RemainderStoriesFr,
    },
};

/** Which set of stories an operator draws from, in the sheet's language. */
function storiesFor(operator: string, hasRemainder: boolean, language: Language): StoryTemplate[] {
    const set = STORIES[language];
    switch (operator) {
        case "-":
            return set.sub;
        case "x":
            return set.mul;
        case "÷":
            // A story about sharing equally is untrue when the numbers do not
            // divide evenly, so those problems get stories that ask what is
            // left over as well.
            return hasRemainder ? set.rem : set.div;
        default:
            return set.add;
    }
}

/**
 * Fills one template.
 *
 * Substitution is onto local strings, never back onto the imported JSON -
 * writing there would edit the template and the next sheet to draw that story
 * would render this problem's numbers.
 */
function fill(
    template: StoryTemplate,
    problem: ProblemData,
    name: string,
    hasRemainder: boolean,
    language: Language
): string[] {
    const [first, second, answer, remainder] = problem.numbers;

    const substitute = (text: string) => {
        // Make the nouns agree before the numbers go in, while the placeholders
        // still mark where each number lands. English templates are written in
        // the plural and need "1 apple" not "1 apples"; the Spanish and French
        // templates are written to need no inflection, so agreeSpanish and
        // agreeFrench are both the identity.
        let agreed = text;
        if (language === "en") {
            agreed = agreeEnglish(agreed, first, "{variable1}");
            agreed = agreeEnglish(agreed, second, "{variable2}");
        } else if (language === "es") {
            agreed = agreeSpanish(agreed);
        } else {
            agreed = agreeFrench(agreed);
        }

        return agreed
            .replaceAll("{variable1}", String(first))
            .replaceAll("{variable2}", String(second))
            .replaceAll("{answer}", String(answer))
            .replaceAll("{remainder}", String(hasRemainder ? remainder : 0))
            .replaceAll("{name}", name);
    };

    return [substitute(template.story), substitute(template.question), substitute(template.problem)];
}

/** Attaches a story to every problem on every page. See `dealStories`. */
export function attachWordProblems(
    pages: ProblemData[][],
    settings: ISettingsProps
): ProblemData[][] {
    const language: Language = printedLanguageOf(settings);
    const names = NAMES[language];

    return dealStories(pages, (problem, pickFrom) => {
        const hasRemainder =
            Boolean(settings.Remainders) && (problem.numbers[3] ?? 0) > 0;
        const template = pickFrom(storiesFor(problem.operator, hasRemainder, language));

        return {
            ...problem,
            display: fill(
                template,
                problem,
                names[getRandomInt(0, names.length - 1)],
                hasRemainder,
                language
            ),
        };
    });
}

/**
 * Stories for money arithmetic (ADR 0063), by operator. Multiplication is an
 * amount times a whole number - four tickets at $3.25 - so {variable2} there is
 * a count, not an amount. Division has no stories because the money generator
 * never makes a division problem.
 */
const MONEY_STORIES: Record<string, StoryTemplate[]> = {
    "+": MoneyAdditionStories,
    "-": MoneySubtractionStories,
    x: MoneyMultiplicationStories,
};

/**
 * Tells money problems as stories about spending, saving and earning.
 *
 * The problems arrive from the money generator with their amounts in cents and
 * already formatted on `display` ("$3.45"), so this substitutes those strings
 * rather than the raw numbers - a story must say $3.45, never 345.
 *
 * English only, whatever the sheet's printed language: the rest of the Money
 * format is not translated either (ADR 0051 §7), and a Spanish story would
 * still be written in dollars. See ADR 0063 §4.
 */
export function attachMoneyWordProblems(pages: ProblemData[][]): ProblemData[][] {
    const names = NAMES.en;

    return dealStories(pages, (problem, pickFrom) => {
        const template = pickFrom(MONEY_STORIES[problem.operator] ?? MONEY_STORIES["+"]);
        const [first = "", second = "", answer = ""] = problem.display ?? [];
        const name = names[getRandomInt(0, names.length - 1)];

        const substitute = (text: string) =>
            text
                .replaceAll("{variable1}", first)
                .replaceAll("{variable2}", second)
                .replaceAll("{answer}", answer)
                .replaceAll("{name}", name);

        return {
            ...problem,
            wordProblem: true,
            display: [
                substitute(template.story),
                substitute(template.question),
                substitute(template.problem),
            ],
        };
    });
}

/**
 * Walks every page giving each problem a story. `pickFrom` deals from a pool
 * without replacement within a page, so a sheet does not ask the same question
 * twice with different numbers. The pool is reshuffled per page; a page asking
 * for more problems than there are stories falls back to repeating rather than
 * coming up short.
 */
function dealStories(
    pages: ProblemData[][],
    tell: (
        problem: ProblemData,
        pickFrom: (pool: StoryTemplate[]) => StoryTemplate
    ) => ProblemData
): ProblemData[][] {
    return pages.map((page) => {
        const pickFrom = pickerForPage();
        return page.map((problem) => tell(problem, pickFrom));
    });
}

/**
 * A picker for one page: draws from a pool without repeating a story the page
 * has already used. Its own function so a Varied page (ADR 0073) can hold the
 * same no-repeat set across several pools. The draws it makes are exactly the
 * ones dealStories always made, in the same order.
 */
function pickerForPage(): (pool: StoryTemplate[]) => StoryTemplate {
    const usedStories = new Set<StoryTemplate>();

    return (pool: StoryTemplate[]) => {
        let template = pool[getRandomInt(0, pool.length - 1)];
        // A handful of tries is enough to avoid a repeat without looping
        // forever when the page is longer than the pool.
        for (let attempt = 0; attempt < 12 && usedStories.has(template); attempt++) {
            template = pool[getRandomInt(0, pool.length - 1)];
        }
        usedStories.add(template);
        return template;
    };
}

/**
 * Tells a Varied sheet (ADR 0073): each operation draws from its ordinary pool
 * and the story types whose unknown is not the result, taken in turn from a
 * random start on every page, so a page of four subtraction problems holds one
 * of each kind. The no-repeat set spans every pool.
 *
 * A problem with a 0 in it falls back to the ordinary pool: "gave some more,
 * and now has 5" is false when the start was 5. Division with a remainder keeps
 * its own pool, which no new type has an equivalent of.
 */
export function attachVariedStories(
    pages: ProblemData[][],
    settings: ISettingsProps
): ProblemData[][] {
    const language: Language = printedLanguageOf(settings);
    const names = NAMES[language];

    return pages.map((page) => {
        const pickFrom = pickerForPage();
        const turn = new Map<string, { start: number; seen: number }>();

        return page.map((problem) => {
            const hasRemainder = Boolean(settings.Remainders) && (problem.numbers[3] ?? 0) > 0;
            const ordinary = storiesFor(problem.operator, hasRemainder, language);

            let pool = ordinary;
            let storyType = "ordinary";
            const types = hasRemainder || !canTakeVariedStory(problem) ? [] : storyTypesFor(problem.operator);
            if (types.length > 0) {
                // The ordinary pool is the first stop of the rotation, then each new type.
                if (!turn.has(problem.operator)) {
                    turn.set(problem.operator, { start: getRandomInt(0, types.length), seen: 0 });
                }
                const state = turn.get(problem.operator)!;
                const stop = (state.start + state.seen) % (types.length + 1);
                state.seen += 1;
                if (stop > 0) {
                    storyType = types[stop - 1];
                    pool = TYPE_STORIES[language][types[stop - 1]];
                }
            }

            const template = pickFrom(pool);
            return {
                ...problem,
                storyType,
                display: fill(
                    template,
                    problem,
                    names[getRandomInt(0, names.length - 1)],
                    hasRemainder,
                    language
                ),
            };
        });
    });
}
